import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  components: {
    // Style the header paper
    MuiPaper: {
      variants: [
        {
          props: { variant: 'header' },
          style: {
            backgroundColor: '#fff',
            borderRadius: 8,
            border: '1px solid #e0e0e0',
            overflow: 'hidden'
          }
        }
      ]
    },
    // Style the audio player container
    MuiBox: {
      variants: [
        {
          props: { variant: 'audioPlayer' },
          style: {
            padding: 16,
            backgroundColor: '#f8f9fa',
            borderRadius: 8,
            border: '1px solid #e0e0e0'
          }
        }
      ]
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#fff',
          borderRight: 'none',
          borderRightWidth: '1px',
          borderRightStyle: 'solid',
          borderRightColor: 'rgba(0, 0, 0, 0.06)',
          height: '100%',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#007050', // Dark green
      light: '#338f77',
      dark: '#00513a',
    },
    secondary: {
      main: '#10b981', // Teal green for real estate
      light: '#34d399',
      dark: '#059669',
    },
    error: {
      main: '#d32f2f', // Red for errors
      light: '#ef5350',
      dark: '#c62828',
    },
    background: {
      default: '#f3f4f6',
      paper: '#ffffff',
      sidebar: '#1e293b', // Dark sidebar
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
      light: '#f8fafc',
    },
    divider: 'rgba(0, 0, 0, 0.06)',
    primaryLink: {
      main: '#0000EE', // Standard browser link blue
      light: '#3333FF',
      dark: '#0000CC',
      hover: '#3333FF',
      active: '#0000CC',
      contrastText: '#ffffff',
    },
    primaryBtn: {
      main: '#0d6efd', // Browser-like primary button blue
      light: '#3d8bfd',
      dark: '#0a58ca',
      hover: '#0b5ed7',
      active: '#0a53be',
      contrastText: '#ffffff',
    },
    secondaryBtn: {
      main: '#6c757d',
      light: '#868e96',
      dark: '#5a6268',
      hover: '#5a6268',
      active: '#545b62',
      contrastText: '#ffffff',
    },
    deleteBtn: {
      main: '#dc3545', // Bootstrap-like danger red
      light: '#e35d6a',
      dark: '#b02a37',
      hover: '#bb2d3b',
      active: '#b02a37',
      contrastText: '#ffffff',
    },
    createBtn: {
      main: '#198754', // Bootstrap-like success green
      light: '#28a745',
      dark: '#146c43',
      hover: '#157347',
      active: '#146c43',
      contrastText: '#ffffff',
    },
    clearBtn: {
      main: '#6c757d', // Standard gray for clear/cancel buttons
      light: '#868e96',
      dark: '#5a6268',
      hover: '#5a6268',
      active: '#545b62',
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#fff',
          borderRight: 'none',
          borderRightWidth: '1px',
          borderRightStyle: 'solid',
          borderRightColor: 'rgba(0, 0, 0, 0.06)',
          height: '100%',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
          marginLeft: 8,
          marginRight: 8,
          '&.Mui-selected': {
            backgroundColor: 'rgba(96, 165, 250, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(96, 165, 250, 0.2)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          textTransform: 'none',
          fontWeight: 500,
          '&:hover': {
            boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.07)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.15)',
          },
        },
        outlinedSecondary: {
          color: '#10b981', // secondary.main
          borderColor: '#10b981', // secondary.main
          '&:hover': {
            backgroundColor: 'rgba(16, 185, 129, 0.04)',
            borderColor: '#059669', // secondary.dark
            color: '#059669', // secondary.dark
          },
        },
      },
      variants: [
        {
          props: { variant: 'primaryBtn' },
          style: {
            backgroundColor: '#0d6efd', // Browser-like primary button blue
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#0b5ed7',
            },
            '&:active': {
              backgroundColor: '#0a53be',
            },
            '&:disabled': {
              backgroundColor: 'rgba(13, 110, 253, 0.5)',
              color: 'rgba(255, 255, 255, 0.8)',
            },
          },
        },
        {
          props: { variant: 'primaryBtnOutlined' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid #0d6efd',
            color: '#0d6efd',
            '&:hover': {
              backgroundColor: 'rgba(13, 110, 253, 0.04)',
              borderColor: '#0b5ed7',
              color: '#0b5ed7',
            },
            '&:active': {
              backgroundColor: 'rgba(13, 110, 253, 0.1)',
              borderColor: '#0a53be',
              color: '#0a53be',
            },
            '&:disabled': {
              borderColor: 'rgba(13, 110, 253, 0.5)',
              color: 'rgba(13, 110, 253, 0.5)',
            },
          },
        },
        {
          props: { variant: 'clearBtn' },
          style: {
            backgroundColor: '#6c757d', // clearBtn.main
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#5a6268', // clearBtn.dark
            },
            '&:active': {
              backgroundColor: '#545b62', // clearBtn.active
            },
            '&:disabled': {
              backgroundColor: 'rgba(108, 117, 125, 0.5)',
              color: 'rgba(255, 255, 255, 0.8)',
            },
          },
        },
        {
          props: { variant: 'clearBtnOutlined' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid #6c757d', // clearBtn.main 
            color: '#6c757d', // clearBtn.main
            '&:hover': {
              backgroundColor: 'rgba(108, 117, 125, 0.04)',
              borderColor: '#5a6268', // clearBtn.dark
              color: '#5a6268', // clearBtn.dark
            },
            '&:active': {
              backgroundColor: 'rgba(108, 117, 125, 0.1)',
              borderColor: '#545b62', // clearBtn.active
              color: '#545b62', // clearBtn.active
            },
            '&:disabled': {
              borderColor: 'rgba(108, 117, 125, 0.5)',
              color: 'rgba(108, 117, 125, 0.5)',
            },
          },
        },
        {
          props: { variant: 'secondaryOutlined' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid #10b981', // secondary.main
            color: '#10b981', // secondary.main
            '&:hover': {
              backgroundColor: 'rgba(16, 185, 129, 0.04)',
              borderColor: '#059669', // secondary.dark
              color: '#059669', // secondary.dark
            },
            '&:active': {
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              borderColor: '#059669', // secondary.dark
              color: '#059669', // secondary.dark
            },
            '&:disabled': {
              borderColor: 'rgba(16, 185, 129, 0.5)',
              color: 'rgba(16, 185, 129, 0.5)',
            },
          },
        },
        {
          props: { variant: 'deleteBtn' },
          style: {
            backgroundColor: '#dc3545', // deleteBtn.main
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#bb2d3b', // deleteBtn.hover
            },
            '&:active': {
              backgroundColor: '#b02a37', // deleteBtn.active
            },
            '&:disabled': {
              backgroundColor: 'rgba(220, 53, 69, 0.5)',
              color: 'rgba(255, 255, 255, 0.8)',
            },
          },
        },
        {
          props: { variant: 'deleteBtnOutlined' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid #dc3545', // deleteBtn.main
            color: '#dc3545', // deleteBtn.main
            '&:hover': {
              backgroundColor: 'rgba(220, 53, 69, 0.04)',
              borderColor: '#bb2d3b', // deleteBtn.hover
              color: '#bb2d3b', // deleteBtn.hover
            },
            '&:active': {
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              borderColor: '#b02a37', // deleteBtn.active
              color: '#b02a37', // deleteBtn.active
            },
            '&:disabled': {
              borderColor: 'rgba(220, 53, 69, 0.5)',
              color: 'rgba(220, 53, 69, 0.5)',
            },
          },
        },
        {
          props: { variant: 'deleteBtnText' },
          style: {
            backgroundColor: 'transparent',
            color: '#dc3545', // deleteBtn.main
            '&:hover': {
              backgroundColor: 'rgba(220, 53, 69, 0.04)',
              color: '#bb2d3b', // deleteBtn.hover
            },
            '&:active': {
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              color: '#b02a37', // deleteBtn.active
            },
            '&:disabled': {
              color: 'rgba(220, 53, 69, 0.5)',
            },
          },
        },
        {
          props: { variant: 'createBtn' },
          style: {
            backgroundColor: '#198754', // createBtn.main
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#157347', // createBtn.hover
            },
            '&:active': {
              backgroundColor: '#146c43', // createBtn.active
            },
            '&:disabled': {
              backgroundColor: 'rgba(25, 135, 84, 0.5)',
              color: 'rgba(255, 255, 255, 0.8)',
            },
          },
        },
        {
          props: { variant: 'createBtnOutlined' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid #198754', // createBtn.main
            color: '#198754', // createBtn.main
            '&:hover': {
              backgroundColor: 'rgba(25, 135, 84, 0.04)',
              borderColor: '#157347', // createBtn.hover
              color: '#157347', // createBtn.hover
            },
            '&:active': {
              backgroundColor: 'rgba(25, 135, 84, 0.1)',
              borderColor: '#146c43', // createBtn.active
              color: '#146c43', // createBtn.active
            },
            '&:disabled': {
              borderColor: 'rgba(25, 135, 84, 0.5)',
              color: 'rgba(25, 135, 84, 0.5)',
            },
          },
        },
        {
          props: { variant: 'createBtnText' },
          style: {
            backgroundColor: 'transparent',
            color: '#198754', // createBtn.main
            '&:hover': {
              backgroundColor: 'rgba(25, 135, 84, 0.04)',
              color: '#157347', // createBtn.hover
            },
            '&:active': {
              backgroundColor: 'rgba(25, 135, 84, 0.1)', 
              color: '#146c43', // createBtn.active
            },
            '&:disabled': {
              color: 'rgba(25, 135, 84, 0.5)',
            },
          },
        },
        {
          props: { variant: 'primaryLinkOutline' },
          style: {
            backgroundColor: 'transparent',
            border: '1px solid #0000EE', // primaryLink.main
            color: '#0000EE', // primaryLink.main
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 238, 0.04)',
              borderColor: '#3333FF', // primaryLink.hover
              color: '#3333FF', // primaryLink.hover
              textDecoration: 'none',
            },
            '&:active': {
              backgroundColor: 'rgba(0, 0, 238, 0.1)',
              borderColor: '#0000CC', // primaryLink.active
              color: '#0000CC', // primaryLink.active
            },
            '&:disabled': {
              borderColor: 'rgba(0, 0, 238, 0.5)',
              color: 'rgba(0, 0, 238, 0.5)',
            },
          },
        },
        {
          props: { variant: 'colorSwatch' },
          style: {
            minWidth: '28px',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            padding: 0,
            margin: '2px',
            '&:hover': {
              opacity: 0.8,
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s',
          },
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.03)',
          borderRadius: 0,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d32f2f',
            borderWidth: '2px',
            backgroundColor: 'rgba(211, 47, 47, 0.04)',
          },
          '& .MuiInputBase-input': {
            fontSize: '13px',
          },
          '& .MuiInputBase-input::placeholder': {
            fontSize: '13px',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '13px',
          '&::placeholder': {
            fontSize: '13px',
          },
        },
        inputCalendarEvent: {
          fontSize: '1.25rem',
          fontWeight: 400,
          padding: '8px 0',
          '&::placeholder': {
            fontSize: '1.25rem',
            opacity: 0.7,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            color: '#d32f2f',
            marginTop: '3px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .Mui-error': {
            color: '#d32f2f',
          },
          '& .MuiInputBase-input': {
            fontSize: '13px',
          },
          '& .MuiInputBase-input::placeholder': {
            fontSize: '13px',
          },
        },
      },
      variants: [
        {
          props: { variant: 'taskField' },
          style: {
            '& .MuiInputBase-root': {
              fontSize: '0.875rem',
              height: '38px',
            },
            '& .MuiInputBase-inputMultiline': {
              height: 'auto',
            },
          },
        },
        {
          props: { variant: 'taskTitle' },
          style: {
            '& .MuiInputBase-root': {
              fontSize: '1.25rem',
              fontWeight: 400,
              '&::before': {
                borderBottomColor: 'divider',
              },
            },
            '& .MuiInput-underline:before': {
              borderBottomColor: 'divider',
            },
          },
        },
      ],
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#1976d2',
          textDecoration: 'none',
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': {
            color: '#2196f3',
            textDecoration: 'none',
          },
          '&:active': {
            color: '#0d47a1',
          },
          '&:focus': {
            outline: 'none',
          },
        },
      },
      variants: [
        {
          props: { variant: 'primaryLink' },
          style: {
            color: '#0000EE', // Standard browser link blue
            textDecoration: 'none',
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': {
              color: '#3333FF',
              textDecoration: 'underline', // Add underline on hover like browser defaults
            },
            '&:active': {
              color: '#0000CC',
            },
            '&:visited': {
              color: '#551A8B', // Visited link color in browsers
            },
          },
        },
      ],
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          minWidth: '180px',
          marginTop: '4px',
          padding: '4px 0',
          borderRadius: '8px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
          elevation: 3,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 16px',
          minHeight: '40px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .MuiListItemIcon-root': {
            minWidth: 'auto',
          },
          '& .MuiListItemText-primary': {
            fontSize: '0.875rem',
            fontWeight: 400,
          },
        },
      },
      variants: [
        {
          props: { variant: 'danger' },
          style: {
            color: 'deleteBtn.main',
            '& .MuiListItemIcon-root': {
              color: 'deleteBtn.main',
            },
            '& .MuiListItemText-primary': {
              color: 'deleteBtn.main',
            },
          },
        },
        {
          props: { variant: 'primary' },
          style: {
            '& .MuiListItemIcon-root': {
              color: 'primaryBtn.main',
            },
          },
        },
        {
          props: { variant: 'create' },
          style: {
            '& .MuiListItemIcon-root': {
              color: 'createBtn.main',
            },
          },
        },
        {
          props: { variant: 'secondary' },
          style: {
            '& .MuiListItemIcon-root': {
              color: 'secondary.main',
            },
          },
        },
        {
          props: { variant: 'gray' },
          style: {
            '& .MuiListItemIcon-root': {
              color: 'secondaryBtn.main',
            },
          },
        },
        {
          props: { variant: 'bold' },
          style: {
            '& .MuiListItemText-primary': {
              fontWeight: 500,
            },
          },
        },
      ],
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '4px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '&.MuiDialog-paperCalendarEvent': {
            borderTopWidth: '4px',
            borderTopStyle: 'solid',
            borderTopColor: 'primary.main',
            borderRadius: '8px',
            maxWidth: '500px',
          },
        },
      },
      variants: [
        {
          props: { variant: 'calendarEvent' },
          style: {
            '& .MuiDialog-paper': {
              borderTopWidth: '4px',
              borderTopStyle: 'solid',
              borderRadius: '8px',
              maxWidth: '500px',
            },
          },
        },
      ],
    },
    MuiFormControl: {
      variants: [
        {
          props: { variant: 'calendarFormRow' },
          style: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            marginBottom: '16px',
          },
        },
      ],
    },
    MuiAutocomplete: {
      variants: [
        {
          props: { variant: 'taskField' },
          style: {
            '& .MuiInputBase-root': {
              fontSize: '0.875rem',
              minHeight: '38px',
            },
            '& .MuiAutocomplete-tag': {
              margin: '2px',
            },
            '& .MuiAutocomplete-option': {
              fontSize: '0.75rem',
              padding: '4px 8px',
            },
          },
        },
      ],
    },
    MuiBox: {
      variants: [
        {
          props: { variant: 'colorPicker' },
          style: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            margin: '8px 0',
          },
        },
        {
          props: { variant: 'colorSwatch' },
          style: {
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              opacity: 0.8,
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s',
          },
        },
        {
          props: { variant: 'formRow' },
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: '16px',
          },
        },
        {
          props: { variant: 'formIcon' },
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            width: '36px',
            marginTop: '12px',
            marginRight: '12px',
            color: 'action.active',
          },
        },
        {
          props: { variant: 'formContent' },
          style: {
            flex: 1,
          },
        },
      ],
    },
  },
});

export default theme; 